import React from 'react';
import { ABLE_COLORS } from '../constants';

/**
 * MabelLogo - Converging dots representing "many → one"
 * 
 * Three dots in progression: small (overwhelm) → medium (processing) → large (clarity)
 * Visually communicates: Understand → Decide → Act
 */
export default function MabelLogo({
  size = 32,
  color = ABLE_COLORS.primary,
  withBackground = false,
  backgroundColor = null
}) {
  // For background version, we want white dots on colored bg
  const dotColor = withBackground ? 'white' : color;
  
  // Calculate proportions matching the icon generator
  // Using 1.35x and 1.8x multipliers for natural progression
  const baseRadius = 4.176; // 5.8% of 72px viewBox width (reduced from 6.5%)
  const smallRadius = baseRadius;
  const mediumRadius = baseRadius * 1.35; // 5.6376
  const largeRadius = baseRadius * 1.8; // 7.5168
  
  // Gap is 1.2x the small radius for optical balance
  const gap = smallRadius * 1.2; // 5.616
  
  // Calculate total width and center the composition
  const totalWidth = smallRadius * 2 + mediumRadius * 2 + largeRadius * 2 + gap * 2;
  const centerY = 18; // Half of 36px viewBox height
  const startX = (72 - totalWidth) / 2 + smallRadius;
  
  // Calculate x positions
  const smallX = startX;
  const mediumX = startX + smallRadius + gap + mediumRadius;
  const largeX = mediumX + mediumRadius + gap + largeRadius;
  
  const logo = (
    <svg
      width={size}
      height={size * 0.5}
      viewBox="0 0 72 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Small dot - overwhelm/many things */}
      <circle 
        cx={smallX}
        cy={centerY}
        r={smallRadius}
        fill={dotColor} 
        opacity={withBackground ? 0.5 : 0.5}
      />
      {/* Medium dot - processing/deciding */}
      <circle 
        cx={mediumX}
        cy={centerY}
        r={mediumRadius}
        fill={dotColor} 
        opacity={withBackground ? 0.75 : 0.75}
      />
      {/* Large dot - clarity/the one action */}
      <circle 
        cx={largeX}
        cy={centerY}
        r={largeRadius}
        fill={dotColor}
        opacity={1.0}
      />
    </svg>
  );

  if (withBackground) {
    const bgSize = size * 1.2;
    return (
      <div style={{
        width: bgSize,
        height: bgSize,
        borderRadius: '22%',
        background: backgroundColor || ABLE_COLORS.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {logo}
      </div>
    );
  }

  return logo;
}
